import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { moodsApi } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';
import { Sparkles, TrendingUp, Calendar, Flame, ChevronLeft, ChevronRight, Edit3, Check, X, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const MOOD_META = {
  1: { label: 'Awful', color: '#ef4444', bgColor: 'rgba(239,68,68,0.15)' },
  2: { label: 'Down', color: '#f97316', bgColor: 'rgba(249,115,22,0.15)' },
  3: { label: 'Okay', color: '#eab308', bgColor: 'rgba(234,179,8,0.15)' },
  4: { label: 'Good', color: '#22c55e', bgColor: 'rgba(34,197,94,0.15)' },
  5: { label: 'Great', color: '#00d4ff', bgColor: 'rgba(0,212,255,0.15)' },
};

const MoodFace = ({ level, size = 32, onClick, selected }) => {
  const meta = MOOD_META[level] || MOOD_META[3];
  const color = meta.color;
  const s = size;
  const cx = s / 2;
  const r = s / 2 - 2;
  const eyeY = cx - r * 0.15;
  const eyeR = r * 0.08;
  const mouthY = cx + r * 0.2;
  const getMouth = () => {
    switch (level) {
      case 1: return <path d={`M ${cx - r * 0.3} ${mouthY + r * 0.15} Q ${cx} ${mouthY - r * 0.2} ${cx + r * 0.3} ${mouthY + r * 0.15}`} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />;
      case 2: return <path d={`M ${cx - r * 0.25} ${mouthY + r * 0.08} Q ${cx} ${mouthY - r * 0.05} ${cx + r * 0.25} ${mouthY + r * 0.08}`} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />;
      case 3: return <line x1={cx - r * 0.2} y1={mouthY} x2={cx + r * 0.2} y2={mouthY} stroke={color} strokeWidth="1.5" strokeLinecap="round" />;
      case 4: return <path d={`M ${cx - r * 0.25} ${mouthY - r * 0.05} Q ${cx} ${mouthY + r * 0.15} ${cx + r * 0.25} ${mouthY - r * 0.05}`} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />;
      case 5: return <path d={`M ${cx - r * 0.3} ${mouthY - r * 0.1} Q ${cx} ${mouthY + r * 0.3} ${cx + r * 0.3} ${mouthY - r * 0.1}`} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />;
      default: return null;
    }
  };
  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} onClick={onClick}
      className={onClick ? `cursor-pointer transition-transform hover:scale-110 ${selected ? 'ring-2 ring-offset-2 ring-offset-navy-900 rounded-full' : ''}` : ''}
      style={selected ? { ringColor: color } : {}}>
      <circle cx={cx} cy={cx} r={r} fill={selected ? meta.bgColor : 'none'} stroke={color} strokeWidth={selected ? '2.5' : '1.5'} />
      <circle cx={cx - r * 0.25} cy={eyeY} r={eyeR} fill={color} />
      <circle cx={cx + r * 0.25} cy={eyeY} r={eyeR} fill={color} />
      {getMouth()}
    </svg>
  );
};

const MoodBar = ({ distribution }) => {
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  if (!total) return null;
  const levels = [5, 4, 3, 2, 1];
  return (
    <div className="flex gap-1 h-4 rounded-full overflow-hidden">
      {levels.map(l => {
        const label = MOOD_META[l].label;
        const count = distribution[label] || 0;
        const pct = (count / total) * 100;
        if (pct === 0) return null;
        return <div key={l} style={{ width: `${pct}%`, backgroundColor: MOOD_META[l].color }} className="transition-all duration-300" title={`${label}: ${count}`} />;
      })}
    </div>
  );
};

export default function MoodHistoryPage() {
  const { t } = useLanguage();
  const [checkins, setCheckins] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editLevel, setEditLevel] = useState(3);
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);
  const LIMIT = 10;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [histRes, analyticsRes] = await Promise.all([
          moodsApi.getAll({ limit: LIMIT, skip: page * LIMIT }),
          moodsApi.getAnalytics()
        ]);
        setCheckins(histRes.data);
        setAnalytics(analyticsRes.data);
      } catch (err) {
        console.error('Failed to fetch mood data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [page]);

  const startEdit = (checkin) => {
    setEditingId(checkin.id);
    setEditLevel(checkin.mood_level);
    setEditNote(checkin.note || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLevel(3);
    setEditNote('');
  };

  const saveEdit = async (moodId) => {
    setSaving(true);
    try {
      const label = MOOD_META[editLevel]?.label || 'Okay';
      const response = await moodsApi.update(moodId, {
        mood_level: editLevel,
        mood_label: label,
        note: editNote
      });
      setCheckins(checkins.map(c => c.id === moodId ? response.data : c));
      setEditingId(null);
      toast.success('Mood entry updated');
      const analyticsRes = await moodsApi.getAnalytics();
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to update mood');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="text-cyan animate-pulse">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="mood-history-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl text-white mb-1">Mood Tracker</h1>
        <p className="text-slate-400">Your emotional wellness journey</p>
      </div>

      {analytics && analytics.total_checkins > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-navy-900/40 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><TrendingUp className="text-cyan" size={18} /><span className="text-slate-400 text-sm">Avg Mood</span></div>
              <p className="text-3xl font-heading text-white">{analytics.average_mood}<span className="text-lg text-slate-500">/5</span></p>
            </CardContent>
          </Card>
          <Card className="bg-navy-900/40 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><Calendar className="text-cyan" size={18} /><span className="text-slate-400 text-sm">Total Check-ins</span></div>
              <p className="text-3xl font-heading text-white">{analytics.total_checkins}</p>
            </CardContent>
          </Card>
          <Card className="bg-navy-900/40 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><Flame className="text-gold" size={18} /><span className="text-slate-400 text-sm">Streak</span></div>
              <p className="text-3xl font-heading text-white">{analytics.streak} <span className="text-lg">days</span></p>
            </CardContent>
          </Card>
          <Card className="bg-navy-900/40 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><Sparkles className="text-gold" size={18} /><span className="text-slate-400 text-sm">Top Mood</span></div>
              <p className="text-xl font-heading text-white">{analytics.most_common_mood || '—'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {analytics?.mood_distribution && Object.keys(analytics.mood_distribution).length > 0 && (
        <Card className="bg-navy-900/40 border-white/5">
          <CardHeader><CardTitle className="text-white text-lg">Mood Distribution</CardTitle></CardHeader>
          <CardContent>
            <MoodBar distribution={analytics.mood_distribution} />
            <div className="flex justify-between mt-3">
              {[5, 4, 3, 2, 1].map(l => {
                const label = MOOD_META[l].label;
                const count = analytics.mood_distribution[label] || 0;
                return (
                  <div key={l} className="flex items-center gap-1 text-xs">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: MOOD_META[l].color }} />
                    <span className="text-slate-400">{label}: {count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-navy-900/40 border-white/5">
        <CardHeader>
          <CardTitle className="text-white text-lg">Recent Check-ins</CardTitle>
        </CardHeader>
        <CardContent>
          {checkins.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No mood check-ins yet. Your first check-in will appear here.</p>
          ) : (
            <div className="space-y-3">
              {checkins.map((c, i) => {
                const meta = MOOD_META[c.mood_level] || MOOD_META[3];
                const isEditing = editingId === c.id;

                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex gap-4 p-4 rounded-xl bg-navy-950/50 border border-white/5 hover:border-white/10 transition-colors"
                    data-testid={`mood-entry-${c.id}`}
                  >
                    {isEditing ? (
                      <div className="flex-1 space-y-3" data-testid={`edit-mood-form-${c.id}`}>
                        <div className="flex items-center gap-3">
                          {[1, 2, 3, 4, 5].map(level => (
                            <MoodFace
                              key={level}
                              level={level}
                              size={40}
                              selected={editLevel === level}
                              onClick={() => setEditLevel(level)}
                            />
                          ))}
                        </div>
                        <Textarea
                          value={editNote}
                          onChange={(e) => setEditNote(e.target.value)}
                          placeholder="What's on your mind?"
                          className="bg-navy-900 border-white/10 text-white resize-none"
                          rows={2}
                          data-testid="edit-mood-note"
                        />
                        <div className="flex gap-2">
                          <Button onClick={() => saveEdit(c.id)} disabled={saving} className="bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full px-5" data-testid="save-mood-edit-btn">
                            {saving ? <Loader2 className="animate-spin" size={16} /> : <><Check size={16} className="mr-1" /> Save</>}
                          </Button>
                          <Button variant="ghost" onClick={cancelEdit} className="text-slate-400 hover:text-white rounded-full" data-testid="cancel-mood-edit-btn">
                            <X size={16} className="mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex-shrink-0 mt-0.5">
                          <MoodFace level={c.mood_level} size={36} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium px-2 py-0.5 rounded-full" style={{ color: meta.color, backgroundColor: meta.bgColor }}>
                              {c.mood_label}
                            </span>
                            <span className="text-xs text-slate-500">
                              {new Date(c.created_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {c.note && <p className="text-slate-300 text-sm mb-2">{c.note}</p>}
                          {c.ai_analysis && (
                            <div className="flex items-start gap-1.5 text-xs text-slate-400">
                              <Sparkles className="text-gold flex-shrink-0 mt-0.5" size={12} />
                              <span>{c.ai_analysis}</span>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => startEdit(c)}
                          className="text-slate-500 hover:text-cyan hover:bg-cyan/10 flex-shrink-0"
                          data-testid={`edit-mood-${c.id}`}
                        >
                          <Edit3 size={16} />
                        </Button>
                      </>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}

          {analytics && analytics.total_checkins > LIMIT && (
            <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-white/5">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} data-testid="mood-prev-page">
                <ChevronLeft size={16} /> Prev
              </Button>
              <span className="text-slate-400 text-sm">Page {page + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => p + 1)} disabled={checkins.length < LIMIT} data-testid="mood-next-page">
                Next <ChevronRight size={16} />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
